import * as React from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useBrandAssets, useBrandProfiles, useBrandTokens, useMe } from "@/api/hooks";
import { createAppTheme } from "@/theme";
import { muiText } from "@/theme/muiText";

/** The org whose brand the signed-in user's app chrome should wear.
 *
 * Admin surfaces have their own org filter; this is for the PRODUCT
 * shell. Uses the pinned active org when there is one, otherwise the
 * user's sole org — a multi-org user with no pin gets the neutral
 * Repoworks look rather than a guess (guessing wrong paints one
 * tenant's colours over another tenant's data).
 */
export function useActiveBrandOrg(): string | null {
  const me = useMe();
  const scope = me.data?.admin_scope;
  if (!scope) return null;
  if (scope.active_org_slug) return scope.active_org_slug;
  const all = [
    ...new Set([
      ...(scope.org_admin_slugs ?? []),
      ...(scope.org_manager_slugs ?? []),
      ...(scope.org_member_slugs ?? []),
    ]),
  ];
  return all.length === 1 ? (all[0] ?? null) : null;
}

function hexFromToken(value: unknown): string | undefined {
  if (typeof value === "string" && /^#[0-9a-fA-F]{6}$/.test(value)) {
    return value;
  }
  if (value && typeof value === "object") {
    const v = value as { hex?: unknown; rgb?: unknown };
    if (typeof v.hex === "string" && /^#[0-9a-fA-F]{6}$/.test(v.hex)) {
      return v.hex;
    }
    if (Array.isArray(v.rgb) && v.rgb.length === 3) {
      const parts = (v.rgb as unknown[]).filter(
        (n): n is number =>
          typeof n === "number" && Number.isInteger(n) && n >= 0 && n <= 255,
      );
      if (parts.length === 3) {
        return `#${parts
          .map((n) => n.toString(16).padStart(2, "0"))
          .join("")}`;
      }
    }
  }
  return undefined;
}

/** The app's own theme — deliberately NOT tenant-tinted.
 *
 * An earlier version repainted the whole MUI palette from the active
 * org's ``color.primary`` / ``color.accent``. That was reverted: the
 * product chrome is Repoworks' own, and letting a tenant's brand
 * recolour every button/link made the app read as a different product
 * per org (and put arbitrary tenant-chosen colours behind controls
 * whose contrast we never validated). Tenant identity now lives where
 * it answers a real question — the org switcher — via
 * ``useActiveOrgBrand`` below, contrast-gated.
 *
 * The theme is rebuilt when the UI language changes, because it carries
 * the text MUI components render themselves (muiText.ts).
 */
export function AppThemeProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const theme = React.useMemo(
    () => createAppTheme({ muiText: muiText((key) => t(key, { lng: language })) }),
    [language, t],
  );
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

/** Brand identity for ONE org, for rendering inside the org switcher.
 *
 * ``color`` is the org's ``color.primary`` when it set one and its
 * primary profile is not just the defaults (else null
 * — the caller falls back to initials, never to a guess), ``logoUrl``
 * its white logo when it uploaded one. Scoped to a single slug so the
 * switcher costs one request, not one per listed org.
 */
export function useActiveOrgBrand(slug: string | null): {
  color: string | null;
  logoUrl: string | null;
} {
  const tokens = useBrandTokens(slug ?? "");
  const assets = useBrandAssets(slug ?? "");
  // FW-47: the colour every new org is seeded with is the default look,
  // not the org's brand — never present it as the org's own.
  const profiles = useBrandProfiles(slug ?? "");
  // Until the list answers, no colour: showing the seeded one for a
  // moment and then dropping it would flicker.
  const profilesPending = Boolean(slug) && profiles.isLoading;
  const primaryIsDefault =
    Array.isArray(profiles.data) &&
    profiles.data.find((p) => p.is_primary)?.brand_status === "default_only";
  const color = React.useMemo(() => {
    if (profilesPending || primaryIsDefault) return null;
    for (const row of tokens.data?.tokens ?? []) {
      if (row.key === "color.primary") return hexFromToken(row.value) ?? null;
    }
    return null;
  }, [tokens.data, profilesPending, primaryIsDefault]);
  const hasLogo = (assets.data?.assets ?? []).some(
    (a: { kind: string }) => a.kind === "logo_white",
  );
  return {
    color: slug ? color : null,
    logoUrl: slug && hasLogo ? `/api/orgs/${slug}/brand/assets/logo_white` : null,
  };
}
